import { AsyncFetchRender } from "src/components/common/async/AsyncFetchRender.tsx";
import { FormCommonProps } from "src/components/common/forms/types.ts";
import { AavoForm, AavoFormContentParams } from "src/components/common/forms/AavoForm.tsx";
import { FormSelectField } from "src/components/common/forms/fields/FormSelectField.tsx";
import { FormTextField } from "src/components/common/forms/fields/FormTextField.tsx";
import { FormCheckbox } from "src/components/common/forms/fields/FormCheckbox.tsx";
import {
	nonNegativeFloatRule,
	requireRule,
	requiredNonNegativeFloatRule,
	requiredNonNegativeIntegerRule,
} from "src/components/common/forms/validation.ts";
import i18n from "i18next";
import { useFieldArray } from "react-hook-form";
import { useEffect } from "react";
import { VerticalBox } from "src/components/common/box/VerticalBox.tsx";
import { HorizontalBox } from "src/components/common/box/HorizontalBox.tsx";
import { AsyncButton } from "src/components/common/buttons/AsyncButton.tsx";
import { faPlus } from "@fortawesome/pro-regular-svg-icons";
import { Box, Divider, IconButton, Typography } from "@mui/material";
import { FrontendDownloadFileHandle } from "src/api/generated/common/frontendDownloadFileHandle.ts";
import { printFile } from "src/utils/filePrinting.ts";
import {
	PurchaseOrderDeliveryPrintoutApi,
	PurchaseOrderDeliveryPrintoutApi_FormInitData,
} from "src/api/generated/erp/purchase/purchaseOrder/api/purchaseOrderDeliveryPrintoutApi.ts";
import { useStoredState } from "src/utils/useStoredState.ts";
import { genericNullableValue } from "src/utils/genericNullableValue.ts";
import { defineAgentUiContextKey } from "src/components/views/ai/assistant/uiContext/agentUiContextKey.ts";
import { DataDirtyStateChangeHandler } from "src/utils/dataDirtyStateChangeHandler.ts";

export interface PurchaseOrderDeliveryPrintoutsFormProps extends FormCommonProps<FrontendDownloadFileHandle | void> {
	purchaseOrderId: number;
	initialPurchaseOrderLineIds?: number[];
	onDataDirtyStateChanged?: DataDirtyStateChangeHandler;
	/** Opened from the subcontracting job queue, which uses the endpoints of the subcontracting permission. */
	subcontracting?: boolean;
}

export const PurchaseOrderDeliveryPrintoutsForm = (props: PurchaseOrderDeliveryPrintoutsFormProps) => {
	return (
		<AsyncFetchRender
			fetch={() =>
				props.subcontracting ?
					PurchaseOrderDeliveryPrintoutApi.getFormInitDataForSubcontracting({
						purchaseOrderId: props.purchaseOrderId,
					})
				:	PurchaseOrderDeliveryPrintoutApi.getFormInitData({ purchaseOrderId: props.purchaseOrderId })
			}
			content={(initData) => <PurchaseOrderDeliveryPrintoutsFormContent initData={initData} {...props} />}
		/>
	);
};

interface FormContentProps extends PurchaseOrderDeliveryPrintoutsFormProps {
	initData: PurchaseOrderDeliveryPrintoutApi_FormInitData;
}

interface PackageFieldValues {
	packageNo: number;
	packageTypeId: number | null;
	description: string;
	weight: number | null;
	length: number | null;
	width: number | null;
	height: number | null;
	lines: PackageLineFieldValues[];
}

interface PackageLineFieldValues {
	purchaseOrderLineId: number | null;
	quantityInPurchaseUnit: number | null;
}

interface FieldValues {
	includeConsignmentNote: boolean;
	cargoDriverId: number | null;
	packages: PackageFieldValues[];
}

const PurchaseOrderDeliveryPrintoutsFormContent = ({
	initData,
	onFormEdited,
	onCompleted,
	onDataDirtyStateChanged,
	purchaseOrderId,
	initialPurchaseOrderLineIds = [],
	subcontracting = false,
}: FormContentProps) => {
	const [storedDefaults, setStoredDefaults] = useStoredState({
		defaultValue: {
			cargoDriverId: genericNullableValue<number>(),
			packageTypeId: genericNullableValue<number>(),
			includeConsignmentNote: false,
		},
		key: "A8F2B3C4D5E6F7G8",
	});
	const hasSavedPrintoutPackages = initData.savedPrintoutPackages.packages.length > 0;
	const defaultCargoDriverId =
		hasSavedPrintoutPackages ?
			(initData.savedPrintoutPackages.cargoDriverId ?? null)
		:	storedDefaults.cargoDriverId;
	const savedPackages = initData.savedPrintoutPackages.packages.map(toPackageFieldValues);
	const nextPackageNo = Math.max(0, ...savedPackages.map((pkg) => pkg.packageNo)) + 1;
	const initialPackage =
		initialPurchaseOrderLineIds.length > 0 ?
			createDefaultPackage(nextPackageNo, storedDefaults.packageTypeId, initialPurchaseOrderLineIds)
		:	null;
	const defaultPackages =
		hasSavedPrintoutPackages ?
			[...savedPackages, ...(initialPackage ? [initialPackage] : [])]
		:	[initialPackage ?? createDefaultPackage(1, storedDefaults.packageTypeId)];

	return (
		<AavoForm<FieldValues, FrontendDownloadFileHandle | void>
			defaultValues={{
				includeConsignmentNote: storedDefaults.includeConsignmentNote ?? false,
				cargoDriverId: defaultCargoDriverId,
				packages: defaultPackages,
			}}
			submit={submit}
			hideCancel
			hideSubmit
			footerExtraComponents={(formParams, triggerSubmit) => (
				<>
					<AsyncButton
						label={i18n.t("close")}
						color={"secondary"}
						onClick={() => formParams.triggerCancel()}
					/>
					<AsyncButton
						label={i18n.t("save")}
						disabled={formParams.formState.disabled}
						onClick={() => triggerSubmit(saveValues)}
					/>
					<AsyncButton
						label={i18n.t("save_and_print")}
						disabled={formParams.formState.disabled}
						onClick={() => triggerSubmit()}
					/>
				</>
			)}
			onFormEdited={onFormEdited}
			onCompleted={onCompleted}
			agentContext={{
				key: defineAgentUiContextKey("erp.purchase.purchaseOrderDeliveryPrintouts.form"),
				data: {
					purchaseOrderId: purchaseOrderId,
				},
			}}
			render={(formParams) => (
				<FormContent initData={initData} onDataDirtyStateChanged={onDataDirtyStateChanged} {...formParams} />
			)}
		/>
	);

	async function submit(values: FieldValues): Promise<FrontendDownloadFileHandle> {
		const params = {
			purchaseOrderId: purchaseOrderId,
			cargoDriverId: values.cargoDriverId,
			packages: toPackageDtos(values),
			includeConsignmentNote: values.includeConsignmentNote,
		};
		const fileHandle =
			subcontracting ?
				await PurchaseOrderDeliveryPrintoutApi.printDeliveryPrintoutsForSubcontracting(params)
			:	await PurchaseOrderDeliveryPrintoutApi.printDeliveryPrintouts(params);
		storeDefaults(values);
		printFile(fileHandle);
		return fileHandle;
	}

	async function saveValues(values: FieldValues) {
		const params = {
			purchaseOrderId: purchaseOrderId,
			cargoDriverId: values.cargoDriverId,
			packages: toPackageDtos(values),
		};
		if (subcontracting) await PurchaseOrderDeliveryPrintoutApi.saveDeliveryPrintoutsForSubcontracting(params);
		else await PurchaseOrderDeliveryPrintoutApi.saveDeliveryPrintouts(params);
		storeDefaults(values);
	}

	function storeDefaults(values: FieldValues) {
		setStoredDefaults({
			cargoDriverId: values.cargoDriverId,
			packageTypeId: values.packages[0]?.packageTypeId ?? null,
			includeConsignmentNote: values.includeConsignmentNote,
		});
	}

	function toPackageDtos(values: FieldValues) {
		return values.packages.map((pkg) => ({
			purchaseOrderDeliveryPrintoutPackageId: null,
			packageNo: pkg.packageNo,
			packageTypeId: pkg.packageTypeId,
			packageTypeName:
				initData.packageTypeOptions.find((p) => p.deliveryPackageTypeId === pkg.packageTypeId)?.name ?? "",
			description: pkg.description || null,
			weight: pkg.weight,
			length: pkg.length,
			width: pkg.width,
			height: pkg.height,
			lines: pkg.lines.flatMap((line) =>
				line.purchaseOrderLineId !== null && line.quantityInPurchaseUnit !== null ?
					[
						{
							purchaseOrderLineId: line.purchaseOrderLineId,
							quantityInPurchaseUnit: line.quantityInPurchaseUnit,
						},
					]
				:	[],
			),
		}));
	}

	function createDefaultPackage(
		packageNo: number,
		packageTypeId: number | null,
		purchaseOrderLineIds: number[] = [],
	): PackageFieldValues {
		const packageType =
			packageTypeId ? initData.packageTypeOptions.find((p) => p.deliveryPackageTypeId === packageTypeId) : null;
		return {
			packageNo,
			packageTypeId: packageTypeId,
			description: "",
			weight: packageType?.packageBaseWeight ?? null,
			length: packageType?.defaultLength ?? null,
			width: packageType?.defaultWidth ?? null,
			height: packageType?.defaultHeight ?? null,
			lines: purchaseOrderLineIds.flatMap((purchaseOrderLineId) => {
				const line = initData.purchaseOrderLineOptions.find(
					(option) => option.purchaseOrderLineId === purchaseOrderLineId,
				);
				return line ?
						[
							{
								purchaseOrderLineId: line.purchaseOrderLineId,
								quantityInPurchaseUnit: line.purchaseQuantity,
							},
						]
					:	[];
			}),
		};
	}

	function toPackageFieldValues(
		pkg: PurchaseOrderDeliveryPrintoutApi_FormInitData["savedPrintoutPackages"]["packages"][number],
	): PackageFieldValues {
		return {
			packageNo: pkg.packageNo,
			packageTypeId: pkg.packageTypeId ?? null,
			description: pkg.description ?? "",
			weight: pkg.weight ?? null,
			length: pkg.length ?? null,
			width: pkg.width ?? null,
			height: pkg.height ?? null,
			lines: pkg.lines.map((line) => ({
				purchaseOrderLineId: line.purchaseOrderLineId,
				quantityInPurchaseUnit: line.quantityInPurchaseUnit,
			})),
		};
	}
};

interface FormContentInnerProps extends AavoFormContentParams<FieldValues> {
	initData: PurchaseOrderDeliveryPrintoutApi_FormInitData;
	onDataDirtyStateChanged?: DataDirtyStateChangeHandler;
}

const FormContent = ({
	initData,
	control,
	formState,
	setValue,
	watch,
	onDataDirtyStateChanged,
}: FormContentInnerProps) => {
	const { fields, append, remove } = useFieldArray({
		control,
		name: "packages",
	});

	const packages = watch("packages");
	const includeConsignmentNote = watch("includeConsignmentNote");

	useEffect(() => {
		onDataDirtyStateChanged?.({
			isDirty: formState.isDirty,
		});
	}, [formState.isDirty, onDataDirtyStateChanged]);

	return (
		<VerticalBox sx={{ gap: 2 }}>
			<FormCheckbox
				control={control}
				name="includeConsignmentNote"
				label={i18n.t("print_consignment_note")}
			/>
			<FormSelectField
				control={control}
				name="cargoDriverId"
				label={i18n.t("cargo_driver")}
				disabled={!includeConsignmentNote}
				options={initData.cargoDriverOptions}
				getOptionKey={(o) => o.cargoDriverId}
				getOptionLabel={(o) => o.cargoDriverName}
			/>

			<Divider />

			<HorizontalBox sx={{ justifyContent: "space-between", alignItems: "center" }}>
				<Typography variant="h6">{i18n.t("packages")}</Typography>
				<AsyncButton
					icon={faPlus}
					label={i18n.t("add_package")}
					variant="outlined"
					onClick={async () => {
						const nextPackageNo = (packages.at(-1)?.packageNo ?? 0) + 1;
						const lastPackageTypeId = packages.at(-1)?.packageTypeId ?? null;
						const packageType =
							lastPackageTypeId ?
								initData.packageTypeOptions.find((p) => p.deliveryPackageTypeId === lastPackageTypeId)
							:	null;
						append({
							packageNo: nextPackageNo,
							packageTypeId: lastPackageTypeId,
							description: "",
							weight: packageType?.packageBaseWeight ?? null,
							length: packageType?.defaultLength ?? null,
							width: packageType?.defaultWidth ?? null,
							height: packageType?.defaultHeight ?? null,
							lines: [],
						});
					}}
				/>
			</HorizontalBox>

			{fields.map((field, index) => (
				<PackageFields
					key={field.id}
					index={index}
					initData={initData}
					control={control}
					watch={watch}
					setValue={setValue}
					onRemove={() => remove(index)}
					canRemove={fields.length > 1}
				/>
			))}
		</VerticalBox>
	);
};

interface PackageFieldsProps {
	index: number;
	initData: PurchaseOrderDeliveryPrintoutApi_FormInitData;
	control: AavoFormContentParams<FieldValues>["control"];
	watch: AavoFormContentParams<FieldValues>["watch"];
	setValue: AavoFormContentParams<FieldValues>["setValue"];
	onRemove: () => void;
	canRemove: boolean;
}

const PackageFields = ({ index, initData, control, watch, setValue, onRemove, canRemove }: PackageFieldsProps) => {
	return (
		<VerticalBox sx={{ gap: 1.5, border: "1px solid", borderColor: "divider", borderRadius: 1 }}>
			<HorizontalBox
				sx={{
					justifyContent: "space-between",
					alignItems: "center",
					backgroundColor: "grey.50",
					borderBottom: "1px solid",
					borderColor: "divider",
					px: 1.5,
					py: 0.75,
				}}
			>
				<Typography variant="subtitle2">
					{i18n.t("package")} {index + 1}
				</Typography>
				{canRemove && (
					<IconButton onClick={onRemove} size="small" sx={{ width: 32, height: 32 }}>
						<span style={{ fontSize: "14px" }}>
							<i className="fa-regular fa-trash" />
						</span>
					</IconButton>
				)}
			</HorizontalBox>

			<VerticalBox sx={{ gap: 1.5, px: 1.5, pb: 1.5 }}>
				<Box
					sx={{
						display: "grid",
						gap: 2,
						gridTemplateColumns: "120px minmax(0, 1fr)",
					}}
				>
					<FormTextField
						control={control}
						name={`packages.${index}.packageNo`}
						label={i18n.t("package_no")}
						rules={requiredNonNegativeIntegerRule()}
						sx={{ width: 120 }}
					/>
					<FormSelectField
						control={control}
						name={`packages.${index}.packageTypeId`}
						label={i18n.t("type")}
						options={initData.packageTypeOptions}
						getOptionKey={(o) => o.deliveryPackageTypeId}
						getOptionLabel={(o) => o.name}
						sx={{ flex: 1 }}
						onChange={(_, option) => {
							setValue(`packages.${index}.length`, option?.defaultLength ?? null);
							setValue(`packages.${index}.width`, option?.defaultWidth ?? null);
							setValue(`packages.${index}.height`, option?.defaultHeight ?? null);
							setValue(`packages.${index}.weight`, option?.packageBaseWeight ?? null);
						}}
					/>
				</Box>

				<FormTextField control={control} name={`packages.${index}.description`} label={i18n.t("description")} />

				<Box
					sx={{
						display: "grid",
						gap: 2,
						gridTemplateColumns: {
							xs: "repeat(2, minmax(0, 1fr))",
							md: "repeat(4, minmax(0, 1fr))",
						},
					}}
				>
					<FormTextField
						control={control}
						name={`packages.${index}.weight`}
						label={i18n.t("weight")}
						rules={nonNegativeFloatRule()}
					/>
					<FormTextField
						control={control}
						name={`packages.${index}.length`}
						label={i18n.t("length_cm")}
						rules={nonNegativeFloatRule()}
					/>
					<FormTextField
						control={control}
						name={`packages.${index}.width`}
						label={i18n.t("width_cm")}
						rules={nonNegativeFloatRule()}
					/>
					<FormTextField
						control={control}
						name={`packages.${index}.height`}
						label={i18n.t("height_cm")}
						rules={nonNegativeFloatRule()}
					/>
				</Box>

				<PackageLineFields
					packageIndex={index}
					initData={initData}
					control={control}
					watch={watch}
					setValue={setValue}
				/>
			</VerticalBox>
		</VerticalBox>
	);
};

interface PackageLineFieldsProps {
	packageIndex: number;
	initData: PurchaseOrderDeliveryPrintoutApi_FormInitData;
	control: AavoFormContentParams<FieldValues>["control"];
	watch: AavoFormContentParams<FieldValues>["watch"];
	setValue: AavoFormContentParams<FieldValues>["setValue"];
}

const PackageLineFields = ({ packageIndex, initData, control, watch, setValue }: PackageLineFieldsProps) => {
	const { fields, append, remove } = useFieldArray({
		control,
		name: `packages.${packageIndex}.lines`,
	});
	const packageLines = watch(`packages.${packageIndex}.lines`);
	const packages = watch("packages");
	const selectedPurchaseOrderLineIds = new Set(
		packages.flatMap((pkg) => pkg.lines.map((line) => line.purchaseOrderLineId).filter((id) => id !== null)),
	);

	return (
		<VerticalBox sx={{ gap: 1 }}>
			<HorizontalBox sx={{ justifyContent: "space-between", alignItems: "center" }}>
				<Typography variant="subtitle2">{i18n.t("content")}</Typography>
				<AsyncButton
					icon={faPlus}
					label={i18n.t("add_package_line")}
					variant="outlined"
					onClick={async () => append(createDefaultPackageLine())}
				/>
			</HorizontalBox>

			{fields.length === 0 && (
				<Box
					sx={{
						border: "1px dashed",
						borderColor: "divider",
						borderRadius: 1,
						color: "text.secondary",
						px: 1.5,
						py: 1,
					}}
				>
					<Typography variant="body2">{i18n.t("table_is_empty")}</Typography>
				</Box>
			)}

			{fields.map((field, lineIndex) => (
				<Box
					key={field.id}
					sx={{
						display: "grid",
						gap: 1.5,
						alignItems: "center",
						gridTemplateColumns: {
							xs: "minmax(0, 1fr) 160px 40px 32px",
							md: "minmax(360px, 1fr) 180px 48px 32px",
						},
					}}
				>
					<FormSelectField
						control={control}
						name={`packages.${packageIndex}.lines.${lineIndex}.purchaseOrderLineId`}
						label={i18n.t("purchase_order_line")}
						options={initData.purchaseOrderLineOptions}
						getOptionKey={(o) => o.purchaseOrderLineId}
						getOptionLabel={(o) => `${i18n.t("line_no")} ${o.lineNumber} - ${o.description}`}
						onChange={(_, option) => {
							setValue(
								`packages.${packageIndex}.lines.${lineIndex}.quantityInPurchaseUnit`,
								option?.purchaseQuantity ?? null,
								{
									shouldDirty: true,
									shouldValidate: true,
								},
							);
						}}
						rules={requireRule()}
						sx={{ minWidth: 0 }}
					/>
					<FormTextField
						control={control}
						name={`packages.${packageIndex}.lines.${lineIndex}.quantityInPurchaseUnit`}
						label={i18n.t("quantity")}
						rules={requiredNonNegativeFloatRule((value) =>
							value > 0 ? true : i18n.t("number_must_be_greater_than_zero"),
						)}
						sx={{ minWidth: 0 }}
					/>
					<Typography>
						{
							initData.purchaseOrderLineOptions.find(
								(option) =>
									option.purchaseOrderLineId === packageLines?.[lineIndex]?.purchaseOrderLineId,
							)?.purchaseUnit
						}
					</Typography>
					<IconButton onClick={() => remove(lineIndex)} size="small" sx={{ width: 32, height: 32 }}>
						<span style={{ fontSize: "14px" }}>
							<i className="fa-regular fa-trash" />
						</span>
					</IconButton>
				</Box>
			))}
		</VerticalBox>
	);

	function createDefaultPackageLine(): PackageLineFieldValues {
		const defaultLine =
			initData.purchaseOrderLineOptions.find(
				(option) => !selectedPurchaseOrderLineIds.has(option.purchaseOrderLineId),
			) ?? null;
		return {
			purchaseOrderLineId: defaultLine?.purchaseOrderLineId ?? null,
			quantityInPurchaseUnit: defaultLine?.purchaseQuantity ?? null,
		};
	}
};
